/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { billingApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, DownloadSimple, CreditCard, TrashSimple, Coins, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtINR(n) {
    if (n == null) return "—";
    return "₹ " + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inv, setInv] = useState(null);
    const [payOpen, setPayOpen] = useState(false);
    const [rzpOpen, setRzpOpen] = useState(false);
    const [rzpInfo, setRzpInfo] = useState(null);

    const load = () => billingApi.getInvoice(id).then(setInv).catch(() => toast.error("Failed to load invoice"));
    useEffect(() => { load(); }, [id]);

    const updateStatus = async (status) => {
        try {
            const updated = await billingApi.updateInvoice(id, { status });
            setInv(updated);
            toast.success(`Status → ${status}`);
        } catch { toast.error("Status update failed"); }
    };
    const remove = async () => {
        try { await billingApi.removeInvoice(id); toast.success("Invoice deleted"); navigate("/billing"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    const openRzp = async () => {
        try {
            const r = await billingApi.razorpayOrder(id);
            setRzpInfo(r);
            setRzpOpen(true);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    if (!inv) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
    const balance = Math.max(0, (inv.net_due || 0) - (inv.paid_amount || 0));

    return (
        <div className="p-4 md:p-8 max-w-[1100px] mx-auto" data-testid="invoice-detail">
            <Link to="/billing" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
                <ArrowLeft size={14} /> Billing
            </Link>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div className="rule-line pt-2">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1 font-mono">{inv.invoice_number}</div>
                    <h1 className="font-serif text-3xl tracking-tight">Invoice to {inv.client_snapshot?.name}</h1>
                    <div className="text-sm text-muted-foreground">{inv.invoice_date} · Due {inv.due_date}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Select value={inv.status} onValueChange={updateStatus}>
                        <SelectTrigger className="w-[160px]" data-testid="status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="sent">Sent</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="partially_paid">Partially paid</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                    <a href={billingApi.invoicePdfUrl(id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-border rounded-sm bg-white px-3 h-9 text-sm hover:bg-muted/30" data-testid="download-pdf">
                        <DownloadSimple size={14} /> PDF
                    </a>
                    <Dialog open={payOpen} onOpenChange={setPayOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" data-testid="record-payment-btn" disabled={inv.status === "cancelled"}><Coins size={14} /> Record payment</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Record payment</DialogTitle>
                                <DialogDescription>Log a manual receipt (cash / bank / cheque / UPI).</DialogDescription>
                            </DialogHeader>
                            <PaymentForm invoice={inv} onDone={(updated) => { setInv(updated); setPayOpen(false); }} />
                        </DialogContent>
                    </Dialog>
                    <Button onClick={openRzp} disabled={balance <= 0} data-testid="razorpay-btn"><CreditCard size={14} /> Razorpay</Button>
                    {(inv.status === "draft" || inv.status === "cancelled") && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-invoice-btn"><TrashSimple size={14} /> Delete</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
                                    <AlertDialogDescription>All linked time entries and expenses will be unmarked so they're available for re-invoicing.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            </div>

            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Subtotal" value={fmtINR(inv.subtotal)} />
                <Stat label={`GST (${inv.gst_mode})`} value={inv.gst_mode === "standard" ? fmtINR(inv.gst_amount) : (inv.gst_mode === "rcm" ? "RCM" : "Exempt")} />
                <Stat label={`TDS @ ${inv.tds_rate}%`} value={fmtINR(inv.tds_amount)} />
                <Stat label="Net due" value={fmtINR(inv.net_due)} tone="emphasis" />
            </div>

            {/* Line items */}
            <section className="border border-border bg-white rounded-md overflow-hidden mb-6">
                <div className="bg-muted text-[10px] uppercase tracking-[0.3em] text-muted-foreground p-2 px-3">Line items</div>
                <table className="w-full text-sm">
                    <thead className="text-muted-foreground border-b border-border">
                        <tr>
                            <th className="text-left p-2 w-8">#</th>
                            <th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Hrs</th>
                            <th className="text-right p-2">Rate</th>
                            <th className="text-right p-2">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(inv.lines || []).map((l, i) => (
                            <tr key={i} className="border-t border-border" data-testid={`inv-line-${i}`}>
                                <td className="p-2 text-xs text-muted-foreground">{i + 1}</td>
                                <td className="p-2">
                                    <div>{l.description}</div>
                                    {l.date && <div className="text-xs text-muted-foreground">{l.date}</div>}
                                </td>
                                <td className="p-2 text-right">{l.hours ?? "—"}</td>
                                <td className="p-2 text-right">{l.rate ? fmtINR(l.rate) : "—"}</td>
                                <td className="p-2 text-right font-medium">{fmtINR(l.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {/* Payments */}
            <section className="border border-border bg-white rounded-md p-3 mb-6" data-testid="payments-section">
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                    Payments received · {fmtINR(inv.paid_amount)} of {fmtINR(inv.net_due)} · balance {fmtINR(balance)}
                </div>
                {!(inv.payments || []).length ? (
                    <div className="text-sm text-muted-foreground">No payments yet.</div>
                ) : (
                    <ul className="space-y-1 text-sm">
                        {inv.payments.map((p) => (
                            <li key={p.id} className="flex items-center gap-3 py-1 border-b border-border/40 last:border-0">
                                <span className="font-medium">{fmtINR(p.amount)}</span>
                                <span className="chip chip-ai">{p.method}</span>
                                <span className="text-xs text-muted-foreground">{p.date}{p.reference ? ` · ${p.reference}` : ""}</span>
                                {p.note && <span className="ml-auto text-xs text-muted-foreground italic">{p.note}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Meta */}
            <section className="text-xs text-muted-foreground space-y-1" data-testid="invoice-meta">
                {inv.advocate_gstin && <div>Advocate GSTIN: {inv.advocate_gstin}</div>}
                {inv.client_gstin && <div>Client GSTIN: {inv.client_gstin}</div>}
                {inv.sac_code && <div>SAC: {inv.sac_code} · Place of supply: {inv.place_of_supply}</div>}
                {inv.notes && <div className="italic">Notes: {inv.notes}</div>}
            </section>

            {/* Razorpay placeholder dialog */}
            <Dialog open={rzpOpen} onOpenChange={setRzpOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Razorpay payment</DialogTitle>
                        <DialogDescription>Online payment collection via Razorpay.</DialogDescription>
                    </DialogHeader>
                    {rzpInfo?.configured ? (
                        <div className="text-sm">Order created: {rzpInfo.razorpay_order_id}</div>
                    ) : (
                        <div className="border border-amber-200 bg-amber-50/60 rounded-sm p-3 text-sm" data-testid="razorpay-placeholder">
                            <div className="flex items-start gap-2 mb-2">
                                <Info size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-medium text-amber-900">Razorpay not configured yet</div>
                                    <div className="text-xs text-amber-900/80 mt-1">{rzpInfo?.message}</div>
                                </div>
                            </div>
                            <div className="border border-border bg-white rounded-sm p-2 text-xs space-y-0.5 font-mono">
                                <div>Amount: ₹{((rzpInfo?.amount_paise || 0) / 100).toLocaleString("en-IN")} ({rzpInfo?.currency})</div>
                                <div>Stub order: {rzpInfo?.stub_order_id}</div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRzpOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, value, tone }) {
    return (
        <div className={`border rounded-md p-3 ${tone === "emphasis" ? "border-amber-300 bg-amber-50/60" : "border-border bg-white"}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            <div className="font-serif text-xl mt-1">{value}</div>
        </div>
    );
}

function PaymentForm({ invoice, onDone }) {
    const balance = Math.max(0, (invoice.net_due || 0) - (invoice.paid_amount || 0));
    const [v, setV] = useState({ amount: balance, method: "bank_transfer", reference: "", date: new Date().toISOString().slice(0, 10), note: "" });
    const submit = async () => {
        const amt = parseFloat(v.amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        try {
            const r = await billingApi.recordPayment(invoice.id, { ...v, amount: amt });
            toast.success("Payment recorded");
            onDone?.(r);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="payment-form">
            <Input type="number" placeholder="Amount (₹)" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} data-testid="pay-amount" />
            <Select value={v.method} onValueChange={(m) => setV({ ...v, method: m })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer / NEFT</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="razorpay">Razorpay</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                </SelectContent>
            </Select>
            <Input placeholder="Reference (txn id / cheque no.)" value={v.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} data-testid="pay-ref" />
            <Input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} />
            <Textarea rows={2} placeholder="Notes (optional)" value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
            <div className="flex justify-end"><Button onClick={submit} data-testid="pay-submit">Record</Button></div>
        </div>
    );
}
